import { useCallback, useEffect, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import axios, { isAxiosError } from 'axios';
import { toast } from 'sonner';

import { getAcceptAttribute } from '@/lib/assets-config';
import http from '@/lib/http';
import { cmsAssetAttachmentPath, cmsDirectUploadPath, cmsDirectUploadsPath } from '@/lib/routes';
import { addWindowEventListener, getCsrfToken, randomId } from '@/lib/utils';
import { INERTIA_ONLY_ASSETS_AND_PAGINATION } from '../constants';
import { calculateFileChecksum, validateFileIntegrity } from '../utils';

export interface UploadingFile {
  id: string;
  file: File;
  progress: number;
  status: 'preparing' | 'uploading' | 'success' | 'error';
  assetId?: string;
  error?: string;
}

export interface UseUploadOptions {
  /** Called when all uploads in the current batch have finished (success or error). */
  onAllComplete?: () => void;
}

const ALLOWED_MIME_TYPES = new Set(Object.keys(getAcceptAttribute('all')));

function getUploadValidationError(file: File): string | null {
  if (file.size <= 0) return 'Empty files cannot be uploaded.';
  if (!file.type)
    return 'This file appears corrupted or has an unknown type and cannot be uploaded.';
  if (!ALLOWED_MIME_TYPES.has(file.type)) return `Unsupported or corrupted file type: ${file.name}`;
  return null;
}

function cleanupIncompleteUpload(assetId: string): void {
  fetch(cmsDirectUploadPath(assetId), {
    method: 'DELETE',
    keepalive: true,
    credentials: 'same-origin',
    headers: {
      'X-CSRF-Token': getCsrfToken(),
      'X-Requested-With': 'XMLHttpRequest',
    },
  }).catch(() => {});
}

export function useUpload(options?: UseUploadOptions) {
  const { onAllComplete } = options ?? {};
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  const pendingAssetIdsRef = useRef<Set<string>>(new Set());
  const isUnloadingRef = useRef(false);
  const onAllCompleteRef = useRef(onAllComplete);
  onAllCompleteRef.current = onAllComplete;

  useEffect(() => {
    const handleBeforeUnload = () => {
      isUnloadingRef.current = true;
      for (const controller of controllersRef.current.values()) {
        controller.abort();
      }
      for (const assetId of pendingAssetIdsRef.current) {
        cleanupIncompleteUpload(assetId);
      }
    };

    const removeListener = addWindowEventListener('beforeunload', handleBeforeUnload);
    return () => removeListener?.();
  }, []);

  useEffect(() => {
    if (
      uploadingFiles.length > 0 &&
      uploadingFiles.every((f) => f.status === 'success' || f.status === 'error')
    ) {
      router.reload({ only: INERTIA_ONLY_ASSETS_AND_PAGINATION });
      onAllCompleteRef.current?.();
    }
  }, [uploadingFiles]);

  const uploadFile = useCallback(async (file: File, folderId?: string | null) => {
    const id = randomId();
    const validationError = getUploadValidationError(file);
    if (validationError) {
      setUploadingFiles((prev) => [
        ...prev,
        { id, file, progress: 0, status: 'error', error: validationError },
      ]);
      toast.error(validationError);
      return;
    }

    setUploadingFiles((prev) => [...prev, { id, file, progress: 0, status: 'preparing' }]);

    const integrityError = await validateFileIntegrity(file);
    if (integrityError) {
      toast.error(integrityError);
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.id === id ? { ...f, status: 'error' as const, error: integrityError } : f,
        ),
      );
      return;
    }

    const controller = new AbortController();
    controllersRef.current.set(id, controller);

    let assetId: string | undefined;

    try {
      const checksum = await calculateFileChecksum(file);

      const prepareResponse = await http.post(
        cmsDirectUploadsPath(),
        {
          filename: file.name,
          contentType: file.type,
          byteSize: file.size,
          checksum,
          ...(folderId ? { folderId } : {}),
        },
        { signal: controller.signal },
      );

      ({ assetId } = prepareResponse.data);
      if (!assetId) throw new Error('Prepare response missing assetId');
      pendingAssetIdsRef.current.add(assetId);
      const { directUpload, blobSignedId } = prepareResponse.data;

      setUploadingFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, status: 'uploading' as const, assetId } : f)),
      );

      await axios.put(directUpload.url, file, {
        headers: {
          ...directUpload.headers,
          'Content-Type': file.type,
        },
        signal: controller.signal,
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadingFiles((prev) => prev.map((f) => (f.id === id ? { ...f, progress } : f)));
        },
      });

      await http.post(cmsAssetAttachmentPath(assetId), { blobSignedId });

      if (assetId) pendingAssetIdsRef.current.delete(assetId);
      controllersRef.current.delete(id);
      setUploadingFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, status: 'success' as const, progress: 100 } : f)),
      );
    } catch (error) {
      if (assetId) pendingAssetIdsRef.current.delete(assetId);
      controllersRef.current.delete(id);
      const isCancelled = isAxiosError(error) && error.code === 'ERR_CANCELED';

      if (isCancelled && assetId && !isUnloadingRef.current) {
        await http.delete(cmsDirectUploadPath(assetId)).catch(() => {});
      }

      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.id === id
            ? {
                ...f,
                status: 'error' as const,
                error: isCancelled
                  ? 'Upload cancelled'
                  : isAxiosError(error)
                    ? (error.response?.data as { message?: string; error?: string } | undefined)
                        ?.message ||
                      (error.response?.data as { message?: string; error?: string } | undefined)
                        ?.error ||
                      error.message
                    : error instanceof Error
                      ? error.message
                      : 'Upload failed',
              }
            : f,
        ),
      );
    }
  }, []);

  const startUploads = useCallback(
    (files: File[], folderId?: string | null) => {
      for (const file of files) {
        void uploadFile(file, folderId);
      }
    },
    [uploadFile],
  );

  const cancelUpload = useCallback((id: string) => {
    controllersRef.current.get(id)?.abort();
    controllersRef.current.delete(id);
  }, []);

  const dismiss = useCallback(() => {
    for (const controller of controllersRef.current.values()) {
      controller.abort();
    }
    controllersRef.current.clear();
    setUploadingFiles([]);
  }, []);

  return { uploadingFiles, startUploads, cancelUpload, dismiss };
}
